import { useQuery } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

import { Sequence } from './sequences_api'

// Shape of the composed read endpoint
// GET /emailbison/campaigns/<campaign_id>/sequence-spec/ (FIR-7659).
// The `sequence` block mirrors the normal sequence read so the wizard can
// hydrate from it through the same path. `schedule` is null when the source
// campaign never set one.

export interface SequenceSpecSenderEmail {
  id: number
  name: string
  email: string
  // 'connected' | 'not_connected' | 'pending_move' | 'pending_deletion'
  status: string
}

export interface SequenceSpecSettings {
  max_emails_per_day: number
  max_new_leads_per_day: number
  plain_text: boolean
  open_tracking: boolean
  reputation_building: boolean
  sequence_prioritization: string
  can_unsubscribe: boolean
  include_auto_replies_in_stats: boolean
}

export interface SequenceSpecSchedule {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
  start_time: string
  end_time: string
  timezone: string
}

export interface SequenceSpec {
  sequence: Sequence | null
  settings: SequenceSpecSettings
  schedule: SequenceSpecSchedule | null
  sender_emails: SequenceSpecSenderEmail[]
}

export const sequenceSpecKeys = {
  all: ['sequence-spec'] as const,
  byCampaign: (campaignId: number) =>
    [...sequenceSpecKeys.all, 'campaign', campaignId] as const,
}

const getCampaignSequenceSpec = async (
  campaignId: number
): Promise<SequenceSpec> => {
  return baseAPICall<SequenceSpec>(() =>
    clientInstance.get(`/emailbison/campaigns/${campaignId}/sequence-spec/`)
  )
}

// Fetch a source campaign's sequence spec. `enabled`-gated so it only fires once
// the user has actually picked a source in the "from existing" flow.
export const useGetCampaignSequenceSpec = (
  campaignId: number | null,
  enabled = true
) => {
  return useQuery({
    queryKey: sequenceSpecKeys.byCampaign(campaignId ?? 0),
    queryFn: () => getCampaignSequenceSpec(campaignId as number),
    enabled: enabled && campaignId != null && campaignId > 0,
  })
}
